// src/components/set-selection-modal/BatchSetSelectionModal.js
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setSystemNotification } from '../../store/system/system.action';
import { useGetSetsQuery, useAddSetItemMutation, useCreateSetMutation } from '../../entities/sets/sets.api';
import { selectUser } from '../../store/user/user.selector';
import InputBar from '../input-bar/InputBar';
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  SetsList,
  SetItem,
  SetCheckbox,
  SetName,
  SetDescription,
  SetStats,
  ModalFooter,
  ButtonGroup,
  AddButton,
  CancelButton,
  LoadingSpinner,
  ErrorMessage
} from './SetSelectionModal.styles';

export default function BatchSetSelectionModal({ 
  isOpen, 
  onClose, 
  cardNumbers,
  filteredCards,
  onSuccess 
}) {
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const [selectedSets, setSelectedSets] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newSetName, setNewSetName] = useState('');
  const [isCreatingSet, setIsCreatingSet] = useState(false);
  
  const { data: sets = [], isLoading, error } = useGetSetsQuery(user?.uid);
  const [addSetItem] = useAddSetItemMutation();
  const [createSet] = useCreateSetMutation();

  // Get card data for selected card numbers
  const selectedCards = cardNumbers
    .map(cardNumber => filteredCards.find(card => card.cardNumber === cardNumber))
    .filter(card => card !== undefined);

  // Reset selected sets and new set name when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedSets([]);
      setNewSetName('');
    }
  }, [isOpen]);

  const handleSetToggle = (setId) => {
    setSelectedSets(prev => 
      prev.includes(setId) 
        ? prev.filter(id => id !== setId)
        : [...prev, setId]
    );
  };

  // Helper function to get item data for a card
  const getItemData = (cardData) => {
    const calculateTotalQuantity = () => {
      if (cardData.variation) {
        return cardData.variation.cardQty || 1;
      } else if (cardData.variations && cardData.variations.length > 0) {
        return cardData.variations.reduce((sum, variation) => sum + (variation.cardQty || 1), 0);
      } else {
        return 1;
      }
    };

    const totalQuantity = calculateTotalQuantity();
    return {
      cardNumber: cardData.cardNumber,
      name: cardData.name,
      team: cardData.team,
      cardAttributes: cardData.cardAttributes || [],
      checklistPath: cardData.checklistPath,
      quantity: totalQuantity,
      variations: cardData.variation ? [cardData.variation] : (cardData.variations || []),
      isCollected: cardData.isCollected !== undefined ? cardData.isCollected : (cardData.variations && cardData.variations.length > 0),
      addedAt: new Date().toISOString(),
      addedBy: user.uid
    };
  };

  const handleCreateSetAndAdd = async () => {
    if (!newSetName.trim()) return;

    setIsCreatingSet(true);
    try {
      // Create the new set
      const setResult = await createSet({
        uid: user.uid,
        setData: {
          title: newSetName.trim(),
          description: '',
          tags: []
        }
      }).unwrap();

      const newSetId = setResult.setId;
      dispatch(setSystemNotification(`Created new set: ${newSetName.trim()}`));

      // Add all selected cards to the newly created set
      const addPromises = selectedCards.map(cardData => {
        const itemId = `${cardData.cardNumber}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const itemData = getItemData(cardData);
        return addSetItem({
          uid: user.uid,
          setId: newSetId,
          itemId,
          data: itemData
        });
      });

      await Promise.all(addPromises);
      dispatch(setSystemNotification('Successfully created set and added cards'));
      onSuccess?.([newSetId]);
      onClose();
    } catch (error) {
      dispatch(setSystemNotification('Error creating set and adding cards'));
    } finally {
      setIsCreatingSet(false);
    }
  };

  const handleAddToSets = async () => {
    if (selectedSets.length === 0) return;

    setIsSubmitting(true);
    try {
      // For each selected set, add all selected cards
      const addPromises = selectedSets.flatMap(setId => 
        selectedCards.map(cardData => {
          const itemId = `${cardData.cardNumber}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          const itemData = getItemData(cardData);
          
          return addSetItem({ 
            uid: user.uid, 
            setId, 
            itemId, 
            data: itemData 
          });
        })
      );

      await Promise.all(addPromises);
      dispatch(setSystemNotification('Successfully added cards to sets'));
      onSuccess?.(selectedSets);
      onClose();
    } catch (error) {
      dispatch(setSystemNotification('Error adding cards to sets'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const totalCards = selectedCards.length;

  const modalContent = (
    <ModalOverlay onClick={onClose} className='widget-bg'>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Add {totalCards} Card{totalCards !== 1 ? 's' : ''} to Sets</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          {isLoading && <LoadingSpinner>Loading sets...</LoadingSpinner>}
          
          {error && <ErrorMessage>Error loading sets: {error.message}</ErrorMessage>}
          
          {sets.length === 0 && !isLoading && (
            <>
              <ErrorMessage>No sets available. Create a set first!</ErrorMessage>
              <p style={{ marginTop: '20px', marginBottom: '8px' }}>
                Create a new set and add {totalCards} card{totalCards !== 1 ? 's' : ''} to it:
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <InputBar
                  type="text"
                  value={newSetName}
                  updateValue={setNewSetName}
                  id="newSetName"
                  label="Set Name"
                  pH="Enter set name"
                  expands={true}
                />
                <AddButton
                  onClick={handleCreateSetAndAdd}
                  disabled={!newSetName.trim() || isCreatingSet}
                  style={{ width: '100%' }}
                >
                  {isCreatingSet ? 'Creating...' : 'CREATE SET'}
                </AddButton>
              </div>
            </>
          )}

          {sets.length > 0 && (
            <>
              <p>
                Select the sets you want to add {totalCards} card{totalCards !== 1 ? 's' : ''} to:
              </p>
              <SetsList>
                {sets.map((set) => (
                  <SetItem key={set.id}>
                    <SetCheckbox
                      type="checkbox"
                      checked={selectedSets.includes(set.id)}
                      onChange={() => handleSetToggle(set.id)}
                    />
                    <div>
                      <SetName>{set.title}</SetName>
                      {set.description && (
                        <SetDescription>{set.description}</SetDescription>
                      )}
                      <SetStats>
                        {set.itemCount || 0} items
                      </SetStats>
                    </div>
                  </SetItem>
                ))}
              </SetsList>
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <ButtonGroup>
            <AddButton 
              onClick={handleAddToSets}
              disabled={selectedSets.length === 0 || isSubmitting}
            >
              {isSubmitting ? 'Adding...' : `Add to ${selectedSets.length} Set${selectedSets.length !== 1 ? 's' : ''}`}
            </AddButton>
            <CancelButton onClick={onClose}>
              Cancel
            </CancelButton>
          </ButtonGroup>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );

  return createPortal(modalContent, document.body);
}
